import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { PortalLayout } from '@/components/layout/PortalLayout';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { LoadingScreen } from '@/components/ui/LoadingScreen';
import { OrganizationLayout } from '@/features/organization/components/OrganizationLayout';
import { PlatformAdminLayout } from '@/features/platform-admin/components/PlatformAdminLayout';
import { WorkspaceLayout } from '@/features/workspaces/components/WorkspaceLayout';
import { ProjectLayout } from '@/features/projects/components/ProjectLayout';
import { CrmLayout } from '@/features/crm/components/CrmLayout';

// Route-level code splitting: a Viewer never downloads the role-management
// bundle, the dashboard's chart libraries don't block the login screen,
// etc. — see documentation/architecture/MASTER_ARCHITECTURE.md §11.
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage').then((m) => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/features/auth/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const VerifyEmailPage = lazy(() =>
  import('@/features/auth/pages/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })),
);
const DashboardPage = lazy(() =>
  import('@/features/dashboard/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const ProfilePage = lazy(() => import('@/features/profile/pages/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const ChangePasswordPage = lazy(() =>
  import('@/features/profile/pages/ChangePasswordPage').then((m) => ({ default: m.ChangePasswordPage })),
);
const TwoFactorSettingsPage = lazy(() =>
  import('@/features/profile/pages/TwoFactorSettingsPage').then((m) => ({ default: m.TwoFactorSettingsPage })),
);
const SessionHistoryPage = lazy(() =>
  import('@/features/sessions/pages/SessionHistoryPage').then((m) => ({ default: m.SessionHistoryPage })),
);
const ActiveDevicesPage = lazy(() =>
  import('@/features/sessions/pages/ActiveDevicesPage').then((m) => ({ default: m.ActiveDevicesPage })),
);
const RoleManagementPage = lazy(() =>
  import('@/features/roles/pages/RoleManagementPage').then((m) => ({ default: m.RoleManagementPage })),
);
const PermissionsPage = lazy(() =>
  import('@/features/permissions/pages/PermissionsPage').then((m) => ({ default: m.PermissionsPage })),
);
const ActivityLogPage = lazy(() =>
  import('@/features/logs/pages/ActivityLogPage').then((m) => ({ default: m.ActivityLogPage })),
);
const SecurityLogPage = lazy(() =>
  import('@/features/logs/pages/SecurityLogPage').then((m) => ({ default: m.SecurityLogPage })),
);
const NotificationCenterPage = lazy(() =>
  import('@/features/notifications/pages/NotificationCenterPage').then((m) => ({ default: m.NotificationCenterPage })),
);
const NotificationSettingsPage = lazy(() =>
  import('@/features/notifications/pages/NotificationSettingsPage').then((m) => ({ default: m.NotificationSettingsPage })),
);
const CalendarPage = lazy(() => import('@/features/calendar/pages/CalendarPage').then((m) => ({ default: m.CalendarPage })));
const HolidaySettingsPage = lazy(() =>
  import('@/features/calendar/pages/HolidaySettingsPage').then((m) => ({ default: m.HolidaySettingsPage })),
);
const TimeTrackingPage = lazy(() =>
  import('@/features/time-tracking/pages/TimeTrackingPage').then((m) => ({ default: m.TimeTrackingPage })),
);
const TimeTrackingSettingsPage = lazy(() =>
  import('@/features/time-tracking/pages/TimeTrackingSettingsPage').then((m) => ({ default: m.TimeTrackingSettingsPage })),
);

// Organization Module
const OrganizationOverviewPage = lazy(() =>
  import('@/features/organization/pages/OrganizationOverviewPage').then((m) => ({ default: m.OrganizationOverviewPage })),
);
const CompanyProfilePage = lazy(() =>
  import('@/features/organization/pages/CompanyProfilePage').then((m) => ({ default: m.CompanyProfilePage })),
);
const BranchesPage = lazy(() => import('@/features/organization/pages/BranchesPage').then((m) => ({ default: m.BranchesPage })));
const DepartmentsPage = lazy(() =>
  import('@/features/organization/pages/DepartmentsPage').then((m) => ({ default: m.DepartmentsPage })),
);
const DesignationsPage = lazy(() =>
  import('@/features/organization/pages/DesignationsPage').then((m) => ({ default: m.DesignationsPage })),
);
const MembersPage = lazy(() => import('@/features/members/pages/MembersPage').then((m) => ({ default: m.MembersPage })));
const EmployeeProfilePage = lazy(() =>
  import('@/features/members/pages/EmployeeProfilePage').then((m) => ({ default: m.EmployeeProfilePage })),
);
const WorkspaceSettingsPage = lazy(() =>
  import('@/features/organization/pages/WorkspaceSettingsPage').then((m) => ({ default: m.WorkspaceSettingsPage })),
);
const LocalizationPage = lazy(() =>
  import('@/features/organization/pages/LocalizationPage').then((m) => ({ default: m.LocalizationPage })),
);
const SmtpSettingsPage = lazy(() =>
  import('@/features/organization/pages/SmtpSettingsPage').then((m) => ({ default: m.SmtpSettingsPage })),
);
const BrandingSettingsPage = lazy(() =>
  import('@/features/organization/pages/BrandingSettingsPage').then((m) => ({ default: m.BrandingSettingsPage })),
);
const ThemeSettingsPage = lazy(() =>
  import('@/features/organization/pages/ThemeSettingsPage').then((m) => ({ default: m.ThemeSettingsPage })),
);
const WorkflowSettingsPage = lazy(() =>
  import('@/features/organization/pages/WorkflowSettingsPage').then((m) => ({ default: m.WorkflowSettingsPage })),
);
const ApiKeysPage = lazy(() => import('@/features/organization/pages/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage })));
const SecuritySettingsPage = lazy(() =>
  import('@/features/organization/pages/SecuritySettingsPage').then((m) => ({ default: m.SecuritySettingsPage })),
);
const BackupSettingsPage = lazy(() =>
  import('@/features/organization/pages/BackupSettingsPage').then((m) => ({ default: m.BackupSettingsPage })),
);
const WebhooksPage = lazy(() => import('@/features/webhooks/pages/WebhooksPage').then((m) => ({ default: m.WebhooksPage })));
const OrganizationReportsPage = lazy(() =>
  import('@/features/organization/pages/OrganizationReportsPage').then((m) => ({ default: m.OrganizationReportsPage })),
);
const AiSettingsPage = lazy(() => import('@/features/ai/pages/AiSettingsPage').then((m) => ({ default: m.AiSettingsPage })));
const BillingOverviewPage = lazy(() =>
  import('@/features/billing/pages/BillingOverviewPage').then((m) => ({ default: m.BillingOverviewPage })),
);
const ChangePlanPage = lazy(() => import('@/features/billing/pages/ChangePlanPage').then((m) => ({ default: m.ChangePlanPage })));
const InvoicesPage = lazy(() => import('@/features/billing/pages/InvoicesPage').then((m) => ({ default: m.InvoicesPage })));
const BillingInvoiceDetailPage = lazy(() =>
  import('@/features/billing/pages/InvoiceDetailPage').then((m) => ({ default: m.InvoiceDetailPage })),
);

// Platform Admin Module — Super Admin only, see components/layout/ProtectedRoute.tsx's requireSuperAdmin.
const PlatformBillingOverviewPage = lazy(() =>
  import('@/features/platform-admin/pages/PlatformBillingOverviewPage').then((m) => ({ default: m.PlatformBillingOverviewPage })),
);
const PlatformPlansPage = lazy(() =>
  import('@/features/platform-admin/pages/PlatformPlansPage').then((m) => ({ default: m.PlatformPlansPage })),
);
const PlatformCouponsPage = lazy(() =>
  import('@/features/platform-admin/pages/PlatformCouponsPage').then((m) => ({ default: m.PlatformCouponsPage })),
);

// Workspace Module — a `Workspace` entity (php-api/modules/workspaces) is
// distinct from the organization-level "Workspace Settings"/switcher
// naming above; imports below are aliased to keep that unambiguous here.
const WorkspacesListPage = lazy(() =>
  import('@/features/workspaces/pages/WorkspacesListPage').then((m) => ({ default: m.WorkspacesListPage })),
);
const WorkspaceDashboardPage = lazy(() =>
  import('@/features/workspaces/pages/WorkspaceDashboardPage').then((m) => ({ default: m.WorkspaceDashboardPage })),
);
const WorkspaceMembersPage = lazy(() =>
  import('@/features/workspaces/pages/WorkspaceMembersPage').then((m) => ({ default: m.WorkspaceMembersPage })),
);
const WorkspaceRolesPage = lazy(() =>
  import('@/features/workspaces/pages/WorkspaceRolesPage').then((m) => ({ default: m.WorkspaceRolesPage })),
);
const WorkspaceFilesPage = lazy(() =>
  import('@/features/workspaces/pages/WorkspaceFilesPage').then((m) => ({ default: m.WorkspaceFilesPage })),
);
const WorkspaceCalendarPage = lazy(() =>
  import('@/features/workspaces/pages/WorkspaceCalendarPage').then((m) => ({ default: m.WorkspaceCalendarPage })),
);
const WorkspaceChatPage = lazy(() =>
  import('@/features/workspaces/pages/WorkspaceChatPage').then((m) => ({ default: m.WorkspaceChatPage })),
);
const WorkspaceAiPage = lazy(() => import('@/features/workspaces/pages/WorkspaceAiPage').then((m) => ({ default: m.WorkspaceAiPage })));
const WorkspaceAnalyticsPage = lazy(() =>
  import('@/features/workspaces/pages/WorkspaceAnalyticsPage').then((m) => ({ default: m.WorkspaceAnalyticsPage })),
);
const WorkspaceEntitySettingsPage = lazy(() =>
  import('@/features/workspaces/pages/WorkspaceSettingsPage').then((m) => ({ default: m.WorkspaceSettingsPage })),
);
const WorkspaceThemePage = lazy(() =>
  import('@/features/workspaces/pages/WorkspaceThemePage').then((m) => ({ default: m.WorkspaceThemePage })),
);

// Project Module
const ProjectsListPage = lazy(() => import('@/features/projects/pages/ProjectsListPage').then((m) => ({ default: m.ProjectsListPage })));
const ProjectDashboardPage = lazy(() =>
  import('@/features/projects/pages/ProjectDashboardPage').then((m) => ({ default: m.ProjectDashboardPage })),
);
const ProjectMilestonesPage = lazy(() =>
  import('@/features/projects/pages/ProjectMilestonesPage').then((m) => ({ default: m.ProjectMilestonesPage })),
);
const ProjectMembersPage = lazy(() =>
  import('@/features/projects/pages/ProjectMembersPage').then((m) => ({ default: m.ProjectMembersPage })),
);
const ProjectFilesPage = lazy(() => import('@/features/projects/pages/ProjectFilesPage').then((m) => ({ default: m.ProjectFilesPage })));
const ProjectCalendarPage = lazy(() =>
  import('@/features/projects/pages/ProjectCalendarPage').then((m) => ({ default: m.ProjectCalendarPage })),
);
const ProjectDiscussionPage = lazy(() =>
  import('@/features/projects/pages/ProjectDiscussionPage').then((m) => ({ default: m.ProjectDiscussionPage })),
);
const ProjectRisksPage = lazy(() => import('@/features/projects/pages/ProjectRisksPage').then((m) => ({ default: m.ProjectRisksPage })));
const ProjectDependenciesPage = lazy(() =>
  import('@/features/projects/pages/ProjectDependenciesPage').then((m) => ({ default: m.ProjectDependenciesPage })),
);
const ProjectReportsPage = lazy(() =>
  import('@/features/projects/pages/ProjectReportsPage').then((m) => ({ default: m.ProjectReportsPage })),
);
const ProjectSettingsPage = lazy(() =>
  import('@/features/projects/pages/ProjectSettingsPage').then((m) => ({ default: m.ProjectSettingsPage })),
);

// Task Management Module
const TasksPage = lazy(() => import('@/features/tasks/pages/TasksPage').then((m) => ({ default: m.TasksPage })));

// Google Meet Module
const MeetingsPage = lazy(() => import('@/features/meetings/pages/MeetingsPage').then((m) => ({ default: m.MeetingsPage })));

// Team Chat Module
const ChatPage = lazy(() => import('@/features/chat/pages/ChatPage').then((m) => ({ default: m.ChatPage })));

// CRM Module
const SalesDashboardPage = lazy(() => import('@/features/crm/pages/SalesDashboardPage').then((m) => ({ default: m.SalesDashboardPage })));
const LeadsListPage = lazy(() => import('@/features/crm/pages/LeadsListPage').then((m) => ({ default: m.LeadsListPage })));
const LeadDetailPage = lazy(() => import('@/features/crm/pages/LeadDetailPage').then((m) => ({ default: m.LeadDetailPage })));
const CompaniesListPage = lazy(() => import('@/features/crm/pages/CompaniesListPage').then((m) => ({ default: m.CompaniesListPage })));
const CompanyDetailPage = lazy(() => import('@/features/crm/pages/CompanyDetailPage').then((m) => ({ default: m.CompanyDetailPage })));
const ContactsListPage = lazy(() => import('@/features/crm/pages/ContactsListPage').then((m) => ({ default: m.ContactsListPage })));
const ContactDetailPage = lazy(() => import('@/features/crm/pages/ContactDetailPage').then((m) => ({ default: m.ContactDetailPage })));
const DealsPage = lazy(() => import('@/features/crm/pages/DealsPage').then((m) => ({ default: m.DealsPage })));
const DealDetailPage = lazy(() => import('@/features/crm/pages/DealDetailPage').then((m) => ({ default: m.DealDetailPage })));
const QuotationsListPage = lazy(() => import('@/features/crm/pages/QuotationsListPage').then((m) => ({ default: m.QuotationsListPage })));
const QuotationDetailPage = lazy(() => import('@/features/crm/pages/QuotationDetailPage').then((m) => ({ default: m.QuotationDetailPage })));
const ProposalsListPage = lazy(() => import('@/features/crm/pages/ProposalsListPage').then((m) => ({ default: m.ProposalsListPage })));
const ProposalDetailPage = lazy(() => import('@/features/crm/pages/ProposalDetailPage').then((m) => ({ default: m.ProposalDetailPage })));
const InvoicesListPage = lazy(() => import('@/features/crm/pages/InvoicesListPage').then((m) => ({ default: m.InvoicesListPage })));
const InvoiceDetailPage = lazy(() => import('@/features/crm/pages/InvoiceDetailPage').then((m) => ({ default: m.InvoiceDetailPage })));

// Documentation Module
const DocumentsPage = lazy(() => import('@/features/documents/pages/DocumentsPage').then((m) => ({ default: m.DocumentsPage })));
const DocumentEditorPage = lazy(() => import('@/features/documents/pages/DocumentEditorPage').then((m) => ({ default: m.DocumentEditorPage })));

// Enterprise File Management Module
const FilesPage = lazy(() => import('@/features/filemanager/pages/FilesPage').then((m) => ({ default: m.FilesPage })));

// Enterprise Reporting System
const ReportsPage = lazy(() => import('@/features/reports/pages/ReportsPage').then((m) => ({ default: m.ReportsPage })));

const SupportTicketsPage = lazy(() =>
  import('@/features/support-tickets/pages/SupportTicketsPage').then((m) => ({ default: m.SupportTicketsPage })),
);

const ClientsListPage = lazy(() => import('@/features/clients/pages/ClientsListPage').then((m) => ({ default: m.ClientsListPage })));
const ClientDetailPage = lazy(() => import('@/features/clients/pages/ClientDetailPage').then((m) => ({ default: m.ClientDetailPage })));

// Client Portal — a fully separate route tree (portalOnly), see
// components/layout/ProtectedRoute.tsx and PortalLayout.tsx headers.
const PortalDashboardPage = lazy(() =>
  import('@/features/client-portal/pages/PortalDashboardPage').then((m) => ({ default: m.PortalDashboardPage })),
);
const PortalProjectsPage = lazy(() =>
  import('@/features/client-portal/pages/PortalProjectsPage').then((m) => ({ default: m.PortalProjectsPage })),
);
const PortalProjectDetailPage = lazy(() =>
  import('@/features/client-portal/pages/PortalProjectDetailPage').then((m) => ({ default: m.PortalProjectDetailPage })),
);
const PortalInvoicesPage = lazy(() =>
  import('@/features/client-portal/pages/PortalInvoicesPage').then((m) => ({ default: m.PortalInvoicesPage })),
);
const PortalMeetingsPage = lazy(() =>
  import('@/features/client-portal/pages/PortalMeetingsPage').then((m) => ({ default: m.PortalMeetingsPage })),
);
const PortalApprovalsPage = lazy(() =>
  import('@/features/client-portal/pages/PortalApprovalsPage').then((m) => ({ default: m.PortalApprovalsPage })),
);
const PortalTicketsPage = lazy(() =>
  import('@/features/client-portal/pages/PortalTicketsPage').then((m) => ({ default: m.PortalTicketsPage })),
);
const PortalTicketDetailPage = lazy(() =>
  import('@/features/client-portal/pages/PortalTicketDetailPage').then((m) => ({ default: m.PortalTicketDetailPage })),
);
const PortalDocumentsPage = lazy(() =>
  import('@/features/client-portal/pages/PortalDocumentsPage').then((m) => ({ default: m.PortalDocumentsPage })),
);

const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));

export function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* Authenticated */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/profile/password" element={<ChangePasswordPage />} />
            <Route path="/profile/two-factor" element={<TwoFactorSettingsPage />} />
            <Route path="/sessions" element={<SessionHistoryPage />} />
            <Route path="/devices" element={<ActiveDevicesPage />} />
            <Route path="/notifications" element={<NotificationCenterPage />} />
            <Route element={<ProtectedRoute requiredPermission="calendar.view" />}>
              <Route path="/calendar" element={<CalendarPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="tasks.view" />}>
              <Route path="/time-tracking" element={<TimeTrackingPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="support_tickets.view" />}>
              <Route path="/support-tickets" element={<SupportTicketsPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="clients.view" />}>
              <Route path="/clients" element={<ClientsListPage />} />
              <Route path="/clients/:id" element={<ClientDetailPage />} />
            </Route>

            <Route element={<ProtectedRoute requiredPermission="roles.view" />}>
              <Route path="/roles" element={<RoleManagementPage />} />
              <Route path="/permissions" element={<PermissionsPage />} />
            </Route>

            <Route path="/logs/activity" element={<ActivityLogPage />} />
            <Route element={<ProtectedRoute requiredPermission="audit_logs.view" />}>
              <Route path="/logs/security" element={<SecurityLogPage />} />
            </Route>

            {/* Organization Module — every member can view the overview;
                individual tabs enforce their own permission via OrganizationLayout's
                nav filtering, and the pages themselves degrade to read-only
                (see each page's `canManage` check) rather than hard-blocking. */}
            <Route path="/organization" element={<OrganizationLayout />}>
              <Route index element={<OrganizationOverviewPage />} />
              <Route element={<ProtectedRoute requiredPermission="organizations.view" />}>
                <Route path="profile" element={<CompanyProfilePage />} />
                <Route path="branches" element={<BranchesPage />} />
                <Route path="departments" element={<DepartmentsPage />} />
                <Route path="designations" element={<DesignationsPage />} />
              </Route>
              <Route element={<ProtectedRoute requiredPermission="users.view" />}>
                <Route path="members" element={<MembersPage />} />
                <Route path="members/:id" element={<EmployeeProfilePage />} />
              </Route>
              <Route element={<ProtectedRoute requiredPermission="settings.view" />}>
                <Route path="workspace-settings" element={<WorkspaceSettingsPage />} />
                <Route path="localization" element={<LocalizationPage />} />
                <Route path="branding" element={<BrandingSettingsPage />} />
                <Route path="theme" element={<ThemeSettingsPage />} />
                <Route path="security" element={<SecuritySettingsPage />} />
              </Route>
              <Route element={<ProtectedRoute requiredPermission="settings.manage" />}>
                <Route path="smtp" element={<SmtpSettingsPage />} />
                <Route path="api-keys" element={<ApiKeysPage />} />
                <Route path="backup" element={<BackupSettingsPage />} />
                <Route path="webhooks" element={<WebhooksPage />} />
                <Route path="workflow" element={<WorkflowSettingsPage />} />
              </Route>
              <Route element={<ProtectedRoute requiredPermission="ai.view" />}>
                <Route path="ai-settings" element={<AiSettingsPage />} />
              </Route>
              <Route element={<ProtectedRoute requiredPermission="reports.view" />}>
                <Route path="reports" element={<OrganizationReportsPage />} />
              </Route>
              <Route element={<ProtectedRoute requiredPermission="notifications.manage" />}>
                <Route path="notifications" element={<NotificationSettingsPage />} />
              </Route>
              <Route element={<ProtectedRoute requiredPermission="calendar.manage" />}>
                <Route path="holidays" element={<HolidaySettingsPage />} />
              </Route>
              <Route element={<ProtectedRoute requiredPermission="time_tracking.manage" />}>
                <Route path="time-tracking" element={<TimeTrackingSettingsPage />} />
              </Route>
              <Route element={<ProtectedRoute requiredPermission="billing.view" />}>
                <Route path="billing" element={<BillingOverviewPage />} />
                <Route path="billing/invoices" element={<InvoicesPage />} />
                <Route path="billing/invoices/:id" element={<BillingInvoiceDetailPage />} />
              </Route>
              <Route element={<ProtectedRoute requiredPermission="billing.manage" />}>
                <Route path="billing/plans" element={<ChangePlanPage />} />
              </Route>
            </Route>

            {/* Platform Admin Module — Super Admin only (users.is_super_admin),
                not an organization permission. See modules/billing/routes.php's
                /platform-admin/* routes and Sidebar.tsx's platformAdminNav. */}
            <Route element={<ProtectedRoute requireSuperAdmin />}>
              <Route path="/platform-admin/billing" element={<PlatformAdminLayout />}>
                <Route index element={<PlatformBillingOverviewPage />} />
                <Route path="plans" element={<PlatformPlansPage />} />
                <Route path="coupons" element={<PlatformCouponsPage />} />
              </Route>
            </Route>

            {/* Workspace Module — list is projects.view-gated (matches how
                modules/workspaces piggybacks on the projects.* permission
                domain); per-tab admin actions degrade via each page's own
                canManage check rather than hard route-level blocking. */}
            <Route element={<ProtectedRoute requiredPermission="projects.view" />}>
              <Route path="/workspaces" element={<WorkspacesListPage />} />
              <Route path="/workspaces/:id" element={<WorkspaceLayout />}>
                <Route index element={<WorkspaceDashboardPage />} />
                <Route path="members" element={<WorkspaceMembersPage />} />
                <Route path="roles" element={<WorkspaceRolesPage />} />
                <Route path="files" element={<WorkspaceFilesPage />} />
                <Route path="calendar" element={<WorkspaceCalendarPage />} />
                <Route path="chat" element={<WorkspaceChatPage />} />
                <Route path="ai" element={<WorkspaceAiPage />} />
                <Route path="analytics" element={<WorkspaceAnalyticsPage />} />
                <Route element={<ProtectedRoute requiredPermission="projects.manage" />}>
                  <Route path="theme" element={<WorkspaceThemePage />} />
                  <Route path="settings" element={<WorkspaceEntitySettingsPage />} />
                </Route>
              </Route>
            </Route>

            {/* Project Module — same projects.view-gated list pattern as
                Workspaces above; per-tab admin actions degrade via each
                page's own canUpdate/canManage check. */}
            <Route element={<ProtectedRoute requiredPermission="projects.view" />}>
              <Route path="/projects" element={<ProjectsListPage />} />
              <Route path="/projects/:id" element={<ProjectLayout />}>
                <Route index element={<ProjectDashboardPage />} />
                <Route path="milestones" element={<ProjectMilestonesPage />} />
                <Route path="members" element={<ProjectMembersPage />} />
                <Route path="files" element={<ProjectFilesPage />} />
                <Route path="calendar" element={<ProjectCalendarPage />} />
                <Route path="discussion" element={<ProjectDiscussionPage />} />
                <Route path="risks" element={<ProjectRisksPage />} />
                <Route path="dependencies" element={<ProjectDependenciesPage />} />
                <Route element={<ProtectedRoute requiredPermission="reports.view" />}>
                  <Route path="reports" element={<ProjectReportsPage />} />
                </Route>
                <Route element={<ProtectedRoute requiredPermission="projects.update" />}>
                  <Route path="settings" element={<ProjectSettingsPage />} />
                </Route>
              </Route>
            </Route>

            {/* Task Management Module — org-wide task views across every project;
                per-task mutations still degrade via tasks.create/update/manage
                checks inside the page and detail panel rather than route-level blocking. */}
            <Route element={<ProtectedRoute requiredPermission="tasks.view" />}>
              <Route path="/tasks" element={<TasksPage />} />
            </Route>

            {/* Google Meet Module — a single page with a calendar/table view
                toggle and a slide-over detail panel, same shape as Tasks'
                single-page-with-modals approach rather than nested routes. */}
            <Route element={<ProtectedRoute requiredPermission="meetings.view" />}>
              <Route path="/meetings" element={<MeetingsPage />} />
            </Route>

            {/* Team Chat Module — a dedicated top-level module (rooms,
                threads, reactions, files, mentions, presence, search),
                separate from WorkspaceChatPage's simpler embedded tab
                which keeps working unchanged against the same backend. */}
            <Route element={<ProtectedRoute requiredPermission="chat.view" />}>
              <Route path="/chat" element={<ChatPage />} />
            </Route>

            {/* CRM Module — section-level tabs (Dashboard/Leads/Companies/
                Contacts/Deals/Quotations/Proposals/Invoices) via CrmLayout,
                same "detail pages render inside the tab shell" pattern as
                Organization's members/:id; per-tab admin actions degrade via
                each page's own canCreate/canUpdate/canDelete/canManage check. */}
            <Route element={<ProtectedRoute requiredPermission="crm.view" />}>
              <Route path="/crm" element={<CrmLayout />}>
                <Route index element={<SalesDashboardPage />} />
                <Route path="leads" element={<LeadsListPage />} />
                <Route path="leads/:id" element={<LeadDetailPage />} />
                <Route path="companies" element={<CompaniesListPage />} />
                <Route path="companies/:id" element={<CompanyDetailPage />} />
                <Route path="contacts" element={<ContactsListPage />} />
                <Route path="contacts/:id" element={<ContactDetailPage />} />
                <Route path="deals" element={<DealsPage />} />
                <Route path="deals/:id" element={<DealDetailPage />} />
                <Route path="quotations" element={<QuotationsListPage />} />
                <Route path="quotations/:id" element={<QuotationDetailPage />} />
                <Route path="proposals" element={<ProposalsListPage />} />
                <Route path="proposals/:id" element={<ProposalDetailPage />} />
                <Route path="invoices" element={<InvoicesListPage />} />
                <Route path="invoices/:id" element={<InvoiceDetailPage />} />
              </Route>
            </Route>
            {/* Documentation Module — a folder-tree browser at /documents
                plus a single editor route with its own side panels
                (comments/versions/approval/AI), same "single page with
                slide-over panels" shape as Meetings/Chat rather than
                CRM's many nested tab routes — the editor's complexity
                lives in panel toggles, not separate URLs. */}
            <Route element={<ProtectedRoute requiredPermission="documents.view" />}>
              <Route path="/documents" element={<DocumentsPage />} />
              <Route path="/documents/:id" element={<DocumentEditorPage />} />
            </Route>

            {/* Enterprise File Management Module — a single page with
                view-state tabs (Browse/Recent/Shared/Favorites/Trash)
                rather than separate routes, same "single page with
                panels" shape as Documents/Chat/Meetings — the folder
                tree and preview modal are what carry the navigation,
                not the URL. */}
            <Route element={<ProtectedRoute requiredPermission="files.view" />}>
              <Route path="/files" element={<FilesPage />} />
            </Route>

            {/* Enterprise Reporting System — a top-level module (distinct
                from Organization → Reports, which stays as the lighter
                4-widget admin overview it always was) covering all 12
                report types, chart-heavy, with export/email/schedule
                actions on every report. */}
            <Route element={<ProtectedRoute requiredPermission="reports.view" />}>
              <Route path="/reports" element={<ReportsPage />} />
            </Route>
          </Route>
        </Route>

        {/* Client Portal — a `client`-role user never renders anything
            above this point (ProtectedRoute redirects them straight
            here); staff never render anything below it. Profile/
            password/notifications reuse the exact same staff page
            components (already personal-scoped, safe as-is) — just
            mounted under PortalLayout instead of AppLayout. */}
        <Route element={<ProtectedRoute portalOnly />}>
          <Route element={<PortalLayout />}>
            <Route path="/portal" element={<PortalDashboardPage />} />
            <Route path="/portal/projects" element={<PortalProjectsPage />} />
            <Route path="/portal/projects/:id" element={<PortalProjectDetailPage />} />
            <Route path="/portal/invoices" element={<PortalInvoicesPage />} />
            <Route path="/portal/meetings" element={<PortalMeetingsPage />} />
            <Route path="/portal/approvals" element={<PortalApprovalsPage />} />
            <Route path="/portal/tickets" element={<PortalTicketsPage />} />
            <Route path="/portal/tickets/:id" element={<PortalTicketDetailPage />} />
            <Route path="/portal/documents" element={<PortalDocumentsPage />} />
            <Route path="/portal/profile" element={<ProfilePage />} />
            <Route path="/portal/profile/password" element={<ChangePasswordPage />} />
            <Route path="/portal/notifications" element={<NotificationCenterPage />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
